/**
 * Outil « Atome et noyau » (Boîte à outils, physique-chimie 3e → Tle).
 *
 * Explorer :
 * - Atome (3e, 2nde) : élément (Z ≤ 18), isotope, nombre d'électrons ; le modèle (noyau,
 *   électrons ; couches K, L, M et électrons de valence en 2nde), l'écriture ᴬ_Z X, la charge,
 *   la configuration électronique, la place dans le tableau et l'ion stable prévu.
 * - Noyau (2nde, Tle) : un noyau qu'on fait se désintégrer (α, β⁻, β⁺) ; équation, conservation
 *   de A et Z, chemin dans le diagramme (N, Z).
 * Défi : cinq questions selon le niveau. Logique : `atom.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, SECONDARY_BUTTON, Segmented, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  ISOTOPES,
  PRESETS,
  atomLines,
  charge,
  configText,
  decay,
  equationText,
  fr,
  makeQuiz,
  nameOf,
  nuclide,
  nucleusLines,
  partsFor,
  position,
  quizKinds,
  shells,
  speciesText,
  stableIon,
  symbolOf,
  decayLabel,
  realMode,
  type Decay,
  type Nucleus,
  type Part,
} from './atom';

const W = 340;
const INK = 'var(--color-ink-700)';
const PROTON = 'var(--color-error-500)';
const NEUTRON = 'var(--color-ink-400)';
const ELECTRON = 'var(--color-primary-500)';
const VALENCE = 'var(--color-accent-600)';

function Lines({ lines }: { lines: string[] }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid="at-law">
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-sm" data-testid="at-readings">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              {k}
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** l'écriture ᴬ_Z X^q en gros */
function Nuclide({ Z, A, q = 0 }: { Z: number; A: number; q?: number }) {
  const c = q === 0 ? '' : `${Math.abs(q) === 1 ? '' : Math.abs(q)}${q > 0 ? '+' : '−'}`;
  return (
    <p
      className="text-ink-900 flex items-center justify-center gap-1 font-bold"
      data-testid="at-nuclide"
      aria-label={`${nameOf(Z)} ${A}${q ? `, charge ${q > 0 ? '+' : '−'}${Math.abs(q)}` : ''}`}
    >
      <span className="flex flex-col items-end text-sm tabular-nums leading-tight" aria-hidden>
        <span>{A}</span>
        <span>{Z}</span>
      </span>
      <span className="font-display text-4xl" aria-hidden>
        {symbolOf(Z)}
      </span>
      {c && (
        <span className="self-start text-base" aria-hidden>
          {c}
        </span>
      )}
    </p>
  );
}

/* ---------- atome ---------- */

function AtomFigure({ Z, A, e, layered }: { Z: number; A: number; e: number; layered: boolean }) {
  const H = 230;
  const cx = W / 2;
  const cy = H / 2;
  const sh = layered ? shells(e) : [e];
  const radii = layered ? [42, 70, 98] : [74];
  const lastShell = sh.length - 1;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Modèle de ${speciesText(Z, Z - e)} : ${Z} protons, ${A - Z} neutrons, ${e} électrons`}
      data-testid="at-figure"
    >
      {sh.map((n, k) => (
        <g key={k}>
          <circle
            cx={cx}
            cy={cy}
            r={radii[k]}
            fill="none"
            stroke="var(--color-ink-200)"
            strokeWidth={1.2}
          />
          {layered && (
            <text x={cx + radii[k]! + 4} y={cy - 4} fontSize={10} fill="var(--color-ink-400)">
              {'KLM'[k]}
            </text>
          )}
          {Array.from({ length: n }, (_, j) => {
            const a = (2 * Math.PI * j) / n - Math.PI / 2;
            return (
              <circle
                key={j}
                cx={cx + radii[k]! * Math.cos(a)}
                cy={cy + radii[k]! * Math.sin(a)}
                r={5}
                fill={layered && k === lastShell ? VALENCE : ELECTRON}
                data-testid="at-electron"
              />
            );
          })}
        </g>
      ))}
      <circle cx={cx} cy={cy} r={22} fill="var(--color-ink-100)" stroke={INK} />
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize={11} fontWeight={700} fill={PROTON}>
        {`${Z} p⁺`}
      </text>
      <text x={cx} y={cy + 11} textAnchor="middle" fontSize={11} fontWeight={700} fill={NEUTRON}>
        {`${A - Z} n`}
      </text>
      <HaloText x={10} y={H - 10} fontSize={10} fill="var(--color-ink-500)">
        {layered ? 'en orange : électrons de valence' : 'noyau (protons, neutrons) et électrons'}
      </HaloText>
    </svg>
  );
}

function AtomView({ rank }: { rank: number | null }) {
  const layered = rank === null || rank >= 5;
  const [Z, setZ] = useState(17);
  const [A, setA] = useState(35);
  const [e, setE] = useState(17);
  const isotopes = ISOTOPES[Z]!;
  const q = charge({ Z, A, e });
  const setElement = (z: number) => {
    setZ(z);
    setA(ISOTOPES[z]![0]!);
    setE(z);
  };
  const p = position(Z);
  const ion = stableIon(Z);
  const rows: Array<[string, string]> = [
    ['Élément', `${nameOf(Z)} (${symbolOf(Z)})`],
    ['Protons (Z)', String(Z)],
    ['Neutrons (N = A − Z)', String(A - Z)],
    ['Électrons', String(e)],
    [
      'Charge',
      q === 0
        ? '0 : atome neutre'
        : `${q > 0 ? '+' : '−'}${Math.abs(q)} : ion ${speciesText(Z, q)}`,
    ],
  ];
  if (layered) {
    rows.push(['Configuration', configText(e)]);
    rows.push(['Place (atome)', `période ${p.period}, colonne ${p.column}`]);
    rows.push(['Ion stable prévu', ion === null ? 'aucun ion simple' : speciesText(Z, ion)]);
    rows.push(['Masse ≈ A × 1,67 × 10⁻²⁷ kg', `${fr(A * 1.67, 2)} × 10⁻²⁷ kg`]);
  }
  return (
    <div className="space-y-4">
      <Nuclide Z={Z} A={A} q={q} />
      <AtomFigure Z={Z} A={A} e={e} layered={layered} />
      <Stepper
        label="Élément (Z)"
        value={Z}
        min={1}
        max={18}
        onChange={setElement}
        format={(z) => `${z} · ${nameOf(z)}`}
      />
      {isotopes.length > 1 && (
        <Segmented
          ariaLabel="Isotope"
          value={String(A)}
          options={isotopes.map((a) => ({ value: String(a), label: `${symbolOf(Z)}-${a}` }))}
          onChange={(v) => setA(Number(v))}
        />
      )}
      <Stepper
        label="Électrons"
        value={e}
        min={Math.max(0, Z - 3)}
        max={Math.min(18, Z + 3)}
        onChange={setE}
      />
      <Readout rows={rows} />
      <Lines lines={atomLines({ Z, A, e }, rank)} />
    </div>
  );
}

/* ---------- noyau ---------- */

function ChainFigure({ path }: { path: Nucleus[] }) {
  const H = 200;
  const Ns = path.map((n) => n.A - n.Z);
  const Zs = path.map((n) => n.Z);
  const nMin = Math.min(...Ns) - 2;
  const nMax = Math.max(...Ns) + 2;
  const zMin = Math.min(...Zs) - 2;
  const zMax = Math.max(...Zs) + 2;
  const s = Math.min((W - 60) / Math.max(nMax - nMin, 6), (H - 50) / Math.max(zMax - zMin, 6));
  const X = (n: number) => 40 + (n - nMin) * s;
  const Y = (z: number) => H - 28 - (z - zMin) * s;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label="Chemin des désintégrations dans le diagramme (N, Z)"
      data-testid="at-chain"
    >
      <line x1={36} y1={H - 24} x2={W - 8} y2={H - 24} stroke="var(--color-ink-300)" />
      <line x1={36} y1={8} x2={36} y2={H - 24} stroke="var(--color-ink-300)" />
      <text x={W - 10} y={H - 8} textAnchor="end" fontSize={10} fill="var(--color-ink-500)">
        N (neutrons) →
      </text>
      <text x={8} y={16} fontSize={10} fill="var(--color-ink-500)">
        Z ↑
      </text>
      {path.slice(1).map((n, i) => (
        <line
          key={i}
          x1={X(path[i]!.A - path[i]!.Z)}
          y1={Y(path[i]!.Z)}
          x2={X(n.A - n.Z)}
          y2={Y(n.Z)}
          stroke={ELECTRON}
          strokeWidth={2}
        />
      ))}
      {path.map((n, i) => (
        <g key={i}>
          <circle
            cx={X(n.A - n.Z)}
            cy={Y(n.Z)}
            r={i === path.length - 1 ? 6 : 4}
            fill={i === path.length - 1 ? VALENCE : INK}
          />
          {(i === 0 || i === path.length - 1) && (
            <HaloText x={X(n.A - n.Z) + 8} y={Y(n.Z) - 6} fontSize={10} fill={INK}>
              {`${symbolOf(n.Z)}-${n.A}`}
            </HaloText>
          )}
        </g>
      ))}
    </svg>
  );
}

function NucleusView() {
  const [preset, setPreset] = useState(0);
  const [path, setPath] = useState<Nucleus[]>([{ Z: PRESETS[0]!.Z, A: PRESETS[0]!.A }]);
  const [decays, setDecays] = useState<Decay[]>([]);
  const cur = path[path.length - 1]!;
  const mode = realMode(cur);
  const apply = (d: Decay) => {
    const f = decay(cur, d);
    if (!f) return;
    setPath([...path, f]);
    setDecays([...decays, d]);
  };
  const choose = (i: number) => {
    setPreset(i);
    setPath([{ Z: PRESETS[i]!.Z, A: PRESETS[i]!.A }]);
    setDecays([]);
  };
  const last =
    decays.length > 0 ? { from: path[path.length - 2]!, d: decays[decays.length - 1]! } : null;
  const nA = decays.filter((d) => d === 'alpha').length;
  const nBm = decays.filter((d) => d === 'beta-').length;
  const nBp = decays.filter((d) => d === 'beta+').length;
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Noyau de départ"
        value={String(preset)}
        options={PRESETS.map((p, i) => ({ value: String(i), label: p.label }))}
        onChange={(v) => choose(Number(v))}
      />
      <Nuclide Z={cur.Z} A={cur.A} />
      <p className="text-ink-600 text-center text-sm">
        {frenchSpaces(`${nameOf(cur.Z)} ${cur.A} : ${cur.Z} protons, ${cur.A - cur.Z} neutrons`)}
      </p>
      <p className="text-ink-600 text-xs font-semibold">Désintégration</p>
      <div className="grid grid-cols-3 gap-2">
        {(['alpha', 'beta-', 'beta+'] as const).map((d) => {
          const ok = mode === null ? decay(cur, d) !== null : mode === d;
          return (
            <button
              key={d}
              type="button"
              className={`${SECONDARY_BUTTON} text-lg disabled:opacity-40`}
              disabled={!ok}
              onClick={() => apply(d)}
              aria-label={`Désintégration ${decayLabel(d)}`}
            >
              {decayLabel(d)}
            </button>
          );
        })}
      </div>
      {mode === 'stable' && (
        <p className="text-success-700 text-sm font-semibold">Noyau stable : fin de la chaîne.</p>
      )}
      {path.length > 1 && <ChainFigure path={path} />}
      {decays.length > 0 && (
        <ol className="text-ink-800 space-y-1 text-sm" data-testid="at-equations">
          {decays.slice(-5).map((d, i) => {
            const k = decays.length - Math.min(5, decays.length) + i;
            return <li key={k}>{equationText(path[k]!, d)}</li>;
          })}
        </ol>
      )}
      <Readout
        rows={[
          ['Désintégrations α', String(nA)],
          ['Désintégrations β⁻', String(nBm)],
          ['Désintégrations β⁺', String(nBp)],
          ['Noyau actuel', nuclide(cur.A, cur.Z, symbolOf(cur.Z))],
        ]}
      />
      <button type="button" className={SECONDARY_BUTTON} onClick={() => choose(preset)}>
        Recommencer
      </button>
      <Lines lines={nucleusLines(cur, last)} />
    </div>
  );
}

function Explore({ rank }: { rank: number | null }) {
  const parts = partsFor(rank);
  const [part, setPart] = useState<Part>(parts[0]!.id);
  return (
    <div className="space-y-4">
      {parts.length > 1 && (
        <Segmented
          ariaLabel="Partie"
          value={part}
          options={parts.map((p) => ({ value: p.id, label: p.label }))}
          onChange={setPart}
        />
      )}
      {part === 'atom' && <AtomView rank={rank} />}
      {part === 'nucleus' && <NucleusView />}
    </div>
  );
}

export function AtomTool({
  level = null,
  seed,
}: {
  /** niveau : parties et notions selon la classe (3e, 2nde, Tle) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="at" rich />
      </div>
    </div>
  );
}
